import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import SEO, { generateSearchSchema } from '../../components/SEO';
import { getImageUrl } from '../../utils/imageUrl';
import FavoriteButton from '../../components/FavoriteButton';
import { Search, MapPin, Filter, X, SlidersHorizontal, ArrowUpDown, Grid3x3 } from 'lucide-react';
import { CATEGORIES, getSubcategories, getCategoryList } from '../../data/categories';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAIN_CATEGORIES = ['Alle', ...Object.keys(CATEGORIES)];

const SORT_OPTIONS = [
  { value: 'relevance', label: 'Relevanz' },
  { value: 'rating', label: 'Bewertung' },
  { value: 'name_asc', label: 'Name A-Z' },
  { value: 'name_desc', label: 'Name Z-A' }
];


const SearchPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');
  const cityParam = searchParams.get('city');
  const zipParam = searchParams.get('zip');

  const [providers, setProviders] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({
    searchText: '',
    category: categoryParam || '',
    subcategory: '',
    location_city: cityParam || '',
    location_zip: zipParam || '',
    priceMin: 0,
    priceMax: 5000,
    sortBy: 'relevance'
  });

  useEffect(() => {
    searchProviders();
  }, [filters.category, filters.location_city, filters.location_zip]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [providers, filters.searchText, filters.priceMin, filters.priceMax, filters.category, filters.sortBy]);

  const searchProviders = async () => {
    setLoading(true);
    try {
      const searchFilters = {
        category: filters.category === 'Alle' ? null : filters.category || null,
        subcategory: filters.subcategory || null,
        location_city: filters.location_city || null,
        location_zip: filters.location_zip || null
      };
      
      const response = await axios.post(`${API}/providers/search`, searchFilters);
      setProviders(response.data);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Fehler bei der Suche');
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...providers];

    // Text search filter
    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase();
      filtered = filtered.filter(p => 
        p.company_name?.toLowerCase().includes(searchLower) ||
        p.short_description?.toLowerCase().includes(searchLower) ||
        p.categories?.some(cat => cat.toLowerCase().includes(searchLower))
      );
    }

    // Price range filter (numeric)
    if (filters.priceMin > 0 || filters.priceMax < 5000) {
      filtered = filtered.filter(p => {
        const price = p.base_price_numeric;
        if (!price) return true; // Show providers without price
        return price >= filters.priceMin && price <= filters.priceMax;
      });
    }

    // Category filter
    if (filters.category && filters.category !== 'Alle') {
      filtered = filtered.filter(p =>
        p.categories?.some(cat => cat.toLowerCase() === filters.category.toLowerCase())
      );
    }

    // Helper function for plan weight
    const getPlanWeight = (planLevel) => {
      const weights = { 'pro': 3, 'plus': 2, 'basic': 1 };
      return weights[planLevel] || 0;
    };

    // Sort with plan level as primary, user choice as secondary
    filtered.sort((a, b) => {
      // Primary: Plan level (Pro > Plus > Basic)
      const planDiff = getPlanWeight(b.plan_level) - getPlanWeight(a.plan_level);
      if (planDiff !== 0) return planDiff;

      // Secondary: User's selected sort
      switch (filters.sortBy) {
        case 'rating':
          return (b.average_rating || 0) - (a.average_rating || 0);
        case 'name_asc':
          return (a.company_name || '').localeCompare(b.company_name || '');
        case 'name_desc':
          return (b.company_name || '').localeCompare(a.company_name || '');
        default: // relevance - use rating as tiebreaker
          return (b.average_rating || 0) - (a.average_rating || 0);
      }
    });

    setFilteredProviders(filtered);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    searchProviders();
  };

  const clearFilter = (filterKey) => {
    setFilters({ ...filters, [filterKey]: '' });
  };

  const hasActiveFilters = filters.searchText || filters.priceMin > 0 || filters.priceMax < 5000 || filters.category || filters.location_city || filters.location_zip;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF0D1]/30 via-white to-[#F4F2F2]">
      <SEO
        title="Event-Dienstleister suchen"
        description="Finde die besten Event-Dienstleister in deiner Nähe. Über 16 Kategorien mit 100+ spezialisierten Dienstleistern für dein perfektes Event."
        keywords="Event Dienstleister suchen, Hochzeit Dienstleister, DJ finden, Fotograf buchen"
        url="/search"
        structuredData={generateSearchSchema()}
      />
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="pt-24 pb-12 px-4">
        <div className="container max-w-7xl">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Event-Dienstleister finden</h1>
            <p className="text-gray-600">Entdecke die besten Event-Profis in deiner Region</p>
          </div>

          {/* Category Quick Select */}
          <div className="bg-white rounded-3xl shadow-xl p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Grid3x3 className="w-5 h-5 text-[#2B2142]" />
                Kategorien
              </h3>
              <button
                onClick={() => navigate('/kategorien')}
                className="text-sm text-[#2B2142] hover:text-[#1E1730] font-semibold"
              >
                Alle anzeigen →
              </button>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
              <button
                onClick={() => setFilters({ ...filters, category: '' })}
                className={`px-4 py-2 rounded-xl font-semibold whitespace-nowrap transition-all flex-shrink-0 ${
                  !filters.category || filters.category === 'Alle'
                    ? 'bg-[#2B2142] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Alle
              </button>
              {getCategoryList().slice(0, 8).map((cat) => (
                <button
                  key={cat}
                  onClick={() => setFilters({ ...filters, category: cat })}
                  className={`px-4 py-2 rounded-xl font-semibold whitespace-nowrap transition-all flex-shrink-0 ${
                    filters.category === cat
                      ? 'bg-[#2B2142] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {cat.length > 25 ? cat.substring(0, 25) + '...' : cat}
                </button>
              ))}
            </div>
          </div>
          
          {/* Search Bar */}
          <div className="bg-white rounded-3xl shadow-xl p-4 sm:p-6 mb-6">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 hidden sm:block w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={filters.searchText}
                  onChange={(e) => setFilters({ ...filters, searchText: e.target.value })}
                  className="w-full pl-4 sm:pl-12 pr-4 py-3 sm:py-4 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none text-base sm:text-lg"
                  placeholder="Suche nach Dienstleister, Kategorie..."
                />
              </div>
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className={`w-full sm:w-auto justify-center px-6 py-3 sm:py-4 font-semibold rounded-xl flex items-center gap-2 transition-colors ${
                  showAdvancedFilters
                    ? 'bg-[#2B2142] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <SlidersHorizontal className="w-5 h-5" />
                Filter
              </button>
            </div>
          </div>

          {/* Advanced Filters */}
          {showAdvancedFilters && (
            <div className="bg-white rounded-3xl shadow-xl p-6 mb-6">
              <form onSubmit={handleSearch} className="space-y-4">
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Hauptkategorie
                  </label>
                  <select
                    data-testid="category-filter"
                    value={filters.category}
                    onChange={(e) => setFilters({ ...filters, category: e.target.value, subcategory: '' })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                  >
                    {MAIN_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat === 'Alle' ? '' : cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                
                {filters.category && filters.category !== 'Alle' && getSubcategories(filters.category).length > 0 && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Unterkategorie
                    </label>
                    <select
                      data-testid="subcategory-filter"
                      value={filters.subcategory}
                      onChange={(e) => setFilters({ ...filters, subcategory: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                    >
                      <option value="">Alle Unterkategorien</option>
                      {getSubcategories(filters.category).map((subcat) => (
                        <option key={subcat} value={subcat}>{subcat}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Stadt
                  </label>
                  <input
                    type="text"
                    data-testid="city-filter"
                    value={filters.location_city}
                    onChange={(e) => setFilters({ ...filters, location_city: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                    placeholder="z.B. Berlin"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    PLZ
                  </label>
                  <input
                    type="text"
                    data-testid="zip-filter"
                    value={filters.location_zip}
                    onChange={(e) => setFilters({ ...filters, location_zip: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                    placeholder="z.B. 10115"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Preisspanne
                  </label>
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                    <span>{filters.priceMin}€</span>
                    <span>{filters.priceMax}€</span>
                  </div>
                  {/* Dual-thumb range slider */}
                  <div className="relative h-2">
                    {/* Track background */}
                    <div className="absolute inset-0 bg-gray-200 rounded-lg"></div>
                    {/* Active range highlight */}
                    <div
                      className="absolute h-full bg-[#2B2142] rounded-lg"
                      style={{
                        left: `${(filters.priceMin / 5000) * 100}%`,
                        right: `${100 - (filters.priceMax / 5000) * 100}%`
                      }}
                    ></div>
                    {/* Min slider */}
                    <input
                      type="range"
                      min="0"
                      max="5000"
                      step="50"
                      value={filters.priceMin}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setFilters({ ...filters, priceMin: Math.min(val, filters.priceMax - 50) });
                      }}
                      className="absolute inset-0 w-full appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-[#2B2142] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-[#2B2142] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:shadow-md"
                    />
                    {/* Max slider */}
                    <input
                      type="range"
                      min="0"
                      max="5000"
                      step="50"
                      value={filters.priceMax}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setFilters({ ...filters, priceMax: Math.max(val, filters.priceMin + 50) });
                      }}
                      className="absolute inset-0 w-full appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-[#2B2142] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-[#2B2142] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:shadow-md"
                    />
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-400 mt-1">
                    <span>0€</span>
                    <span>5000€</span>
                  </div>
                </div>
              </div>
              
              <button
                type="submit"
                data-testid="search-btn"
                disabled={loading}
                className="w-full py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Search className="w-5 h-5" />
                {loading ? 'Sucht...' : 'Suchen'}
              </button>
            </form>
            </div>
          )}
          
          {/* Active Filters & Sort */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex flex-wrap gap-2">
              {filters.category && (
                <div className="flex items-center gap-2 px-4 py-2 bg-[#FFF0D1] text-[#2B2142] rounded-full text-sm font-semibold">
                  <span>{filters.category}</span>
                  <button onClick={() => clearFilter('category')} className="hover:bg-[#2B2142]/20 rounded-full p-0.5">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              {filters.location_city && (
                <div className="flex items-center gap-2 px-4 py-2 bg-[#FFF0D1] text-[#2B2142] rounded-full text-sm font-semibold">
                  <MapPin className="w-4 h-4" />
                  <span>{filters.location_city}</span>
                  <button onClick={() => clearFilter('location_city')} className="hover:bg-[#2B2142]/20 rounded-full p-0.5">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              {filters.location_zip && (
                <div className="flex items-center gap-2 px-4 py-2 bg-[#FFF0D1] text-[#2B2142] rounded-full text-sm font-semibold">
                  <MapPin className="w-4 h-4" />
                  <span>PLZ: {filters.location_zip}</span>
                  <button onClick={() => clearFilter('location_zip')} className="hover:bg-[#2B2142]/20 rounded-full p-0.5">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              {(filters.priceMin > 0 || filters.priceMax < 5000) && (
                <div className="flex items-center gap-2 px-4 py-2 bg-[#FFF0D1] text-[#2B2142] rounded-full text-sm font-semibold">
                  <span>{filters.priceMin}€ - {filters.priceMax}€</span>
                  <button onClick={() => setFilters({ ...filters, priceMin: 0, priceMax: 5000 })} className="hover:bg-[#2B2142]/20 rounded-full p-0.5">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <ArrowUpDown className="w-4 h-4" />
                Sortieren:
              </label>
              <select
                value={filters.sortBy}
                onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
                className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none text-sm font-semibold"
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Results */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2B2142]"></div>
            </div>
          ) : (
          <div>
            <p className="text-gray-600 mb-6">{filteredProviders.length} von {providers.length} Dienstleistern</p>

            {filteredProviders.length === 0 ? (
              <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
                <Filter className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Keine Dienstleister gefunden</h3>
                <p className="text-gray-600">
                  {providers.length === 0 
                    ? 'Versuche andere Filter oder eine andere Region'
                    : 'Keine Treffer für deine Suchkriterien. Versuche andere Filter.'}
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProviders.map((provider) => (
                  <div
                    key={provider.id}
                    className="bg-white rounded-2xl shadow-lg hover:shadow-2xl border-2 border-transparent hover:border-[#2B2142] cursor-pointer group overflow-hidden"
                    onClick={() => navigate(`/provider/${provider.id}`)}
                    data-testid={`provider-card-${provider.id}`}
                  >
                    <div className="relative">
                      {/* Favorite Button */}
                      <div
                        className="absolute top-2 right-2 z-10"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FavoriteButton providerId={provider.id} size="small" />
                      </div>
                      {(() => {
                        // Use profile_picture if available, otherwise first gallery image
                        const displayImage = provider.profile_picture || (provider.images && provider.images.length > 0 ? provider.images[0] : null);
                        const isProfilePicture = provider.profile_picture && displayImage === provider.profile_picture;

                        return displayImage ? (
                          <div className="h-48 overflow-hidden bg-white flex items-center justify-center">
                            <img
                              src={getImageUrl(displayImage)}
                              alt={provider.company_name}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                              onError={(e) => {
                                console.error('Search image load error:', displayImage);
                                e.target.parentElement.innerHTML = `
                                  <div class="h-48 bg-gradient-to-br from-[#FFF0D1] to-[#F4F2F2] flex items-center justify-center">
                                    <p class="text-4xl font-bold text-gray-300">${provider.company_name?.charAt(0) || '?'}</p>
                                  </div>
                                `;
                              }}
                            />
                          </div>
                        ) : (
                          <div className="h-48 bg-gradient-to-br from-[#FFF0D1] to-[#F4F2F2] flex items-center justify-center">
                            <p className="text-4xl font-bold text-gray-300">
                              {provider.company_name?.charAt(0) || '?'}
                            </p>
                          </div>
                        );
                      })()}
                    </div>
                    
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-xl font-bold text-gray-900 flex-1">{provider.company_name}</h3>
                        {provider.plan_level === 'pro' && (
                          <span className="px-3 py-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold rounded-full ml-2">
                            🏆 TOP
                          </span>
                        )}
                        {provider.plan_level === 'plus' && (
                          <span className="px-3 py-1 bg-gradient-to-r from-[#2B2142] to-[#3D3158] text-white text-xs font-bold rounded-full ml-2">
                            ⭐ EMPFOHLEN
                          </span>
                        )}
                      </div>
                      
                      <div className="flex gap-2 flex-wrap mb-3">
                        {provider.categories?.slice(0, 2).map((cat) => (
                          <span key={cat} className="px-2 py-1 bg-[#FFF0D1]/30 text-[#2B2142] rounded-lg text-xs font-semibold">
                            {cat}
                          </span>
                        ))}
                        {provider.categories?.length > 2 && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold">
                            +{provider.categories.length - 2}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 text-gray-600 mb-3">
                        <MapPin className="w-4 h-4" />
                        <span className="text-sm">{provider.location_city}</span>
                      </div>
                      
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">{provider.short_description}</p>
                      
                      {provider.base_price_from && (
                        <p className="text-[#2B2142] font-semibold text-sm">{provider.base_price_from}</p>
                      )}
                      
                      <button
                        className="mt-4 w-full py-2 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-lg"
                        data-testid={`view-profile-${provider.id}`}
                      >
                        Profil ansehen
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SearchPage;
